import React from "react";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import RichText from "@qlik/qlik/src/components/RichText";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

/**
 * CardPromoFullWidth component.
 * Renders a full width promotional card with title, description, body, cta, media and background color.
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data for the card.
 * @returns {JSX.Element} The rendered component.
 */
const CardPromoFullWidth = ({ data }) => {
    // Destructuring the data object to get the required properties
    const { customId, cardItem, backgroundColor, additionalConfiguration } = data ?? {};
    const { title, description, body, cta, media } = cardItem ?? {};

    // Determine the order of the media and text based on the additional configuration
    const orderMediaText = String(additionalConfiguration?.contentOrder) === "Media/Text";

    // Utility classes for order of media and text
    const mediaOrderClass = orderMediaText ? "md:flex-row" : "md:flex-row-reverse";
    // const mediaOrderClass = "md:flex-row-reverse";

    // Utility classes to adjust layout of copy and media
    const copySwitchClass = orderMediaText ? "lg:pr-0" : "lg:pl-0";
    const mediaSwitchClass = orderMediaText ? "lg:justify-end" : "";

    // Getting the class name for section spacing
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration, "py-8 md:py-0");

    // Setting the background color based on the backgroundColor property
    const { sectionColorMapping } = getColorClassName(backgroundColor);

    // Determining styles based on media
    const noImageStyles =
        media?.[0]?.desktopMedia?.length || media?.[0]?.mobileMedia?.length || media?.[0]?.mediaCoverImage?.length
            ? "md:w-3/5 pt-8 rounded-b md:rounded-br-none md:rounded-l-sm"
            : "md:grow pt-0 lg:pr-0";

    // Rendering the component
    return (
        <section
            id={customId}
            data-section
            className={`promoCardFullWidth card-promo-full-width scroll-mt-45 contain-paint py-8 md:py-0 ${sectionSpacingClassName} ${sectionColorMapping.bgColor}`}
        >
            <div className="container">
                <div className={`card-wrapper md:flex ${mediaOrderClass}`}>
                    {/* Rendering the media if it exists */}
                    {(media?.[0]?.desktopMedia?.length ||
                        media?.[0]?.mobileMedia?.length ||
                        media?.[0]?.mediaCoverImage?.length) && (
                        <div
                            className={`md:w-2/5 md:py-0 md:flex md:items-center md:pl-1.25 md:relative ${mediaSwitchClass} ${styles.mediaWrapper}`}
                        >
                            {media?.[0] && (
                                <Media media={media?.[0]} className="w-full object-cover xl:w-[665px] xl:max-w-none" />
                            )}
                        </div>
                    )}
                    <div className={`px-0 md:p-8 lg:p-15 ${copySwitchClass} ${styles.contentWrapper} ${noImageStyles}`}>
                        {/* Rendering the title if it exists */}
                        {title && (
                            <h3
                                className={`${styles.cardPromoTitle} title-3 mb-3.5 ${sectionColorMapping.primaryTextColor}`}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                            />
                        )}

                        {/* Rendering the description if it exists */}
                        {description?.description && (
                            <p className={`${sectionColorMapping.primaryTextColor}`}>{description.description}</p>
                        )}

                        {/* Rendering the body if it exists */}
                        {body && (
                            <RichText
                                data={cardItem}
                                className={`${styles.bodyContent} ${sectionColorMapping.primaryTextColor}`}
                            />
                        )}

                        {/* Rendering the CTA if it exists */}
                        {cta && (
                            <div className="col-12 mt-5.5 md:mt-7.5 flex gap-2 justify-start flex-wrap">
                                {cta.map((ctaItem) => (
                                    <Cta key={ctaItem.id} data={ctaItem} className="tracking-normal" />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CardPromoFullWidth;
